/**
 *  Scanning service
 */
import { Injectable } from '@angular/core';
import { getAllRowsAfterFilterAndSort } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';

import { observableFromEvent, rx, Subscription } from '@app/shared/rx';
import { EInputEvent, NotificationType } from '@app/shared/types';
import { UiActions } from '@app/store/actions';
import {
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
  DEFAULT_VALID_SCANNING_LENGTH,
  ENTITY_ID_PATH,
  KEY_CODES,
  MAX_SCREEN_SHOW_ITEMS,
} from '@config/global-constants';
import { ColumnApi, GridApi, RowNode } from 'ag-grid-community';
import {
  chain,
  eq,
  filter,
  find,
  forEach,
  get,
  gte,
  head,
  includes,
  isEmpty,
  isNil,
  lt,
  map,
  size,
  toLower,
} from 'lodash';
import {
  EnsureIndexVisiblePosition,
  IScanningRenderOptions,
  IScanResultOption,
} from './scanning.types';
import { judgeScanned } from '@app/shared/utils';
import { StateService } from '../state-service';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class ScanningService {
  public keyTimes: number[] = [];
  public gridApi: GridApi;
  public columnApi: ColumnApi;
  public inventoryItems: RowNode[];
  public scannedItemId: string = '';
  // for list view, first scan less 20 need refresh list
  public firstScanLessFifty: boolean = false;

  public callBack: Function;

  public ignoreScanningKeyList: string[] = [
    KEY_CODES.BACK_SPACE,
    KEY_CODES.ARROW_UP,
    KEY_CODES.ARROW_DOWN,
    KEY_CODES.ARROW_LEFT,
    KEY_CODES.ARROW_RIGHT,
    KEY_CODES.ENTER,
    KEY_CODES.TAB,
    KEY_CODES.CLEAR,
  ];

  public scanOptions: IScanResultOption = null;

  public constructor(
    public uiActions: UiActions,
    private stateService: StateService,
    public i18nService: XVII18nService,
  ) {}

  public returnScanningSubscription(
    scanResultOption: IScanResultOption,
  ): Subscription {
    const keydownEvent$ = observableFromEvent(document, EInputEvent.KEYDOWN);

    const subs = keydownEvent$
      .pipe(
        rx.filter((event) => {
          const isUseCustomScanning = eq(
            (event.target as HTMLInputElement).getAttribute('customScanning'),
            'true',
          );

          return !isUseCustomScanning;
        }),
        rx.buffer(
          keydownEvent$.pipe(
            rx.debounceTime(DEFAULT_ARTIFICIAL_INPUT_THRESHOLD),
          ),
        ),
        // we remove these keys because their are frequent events, when user input text
        rx.map((events: KeyboardEvent[]) => {
          return filter(
            events,
            (event) => !includes(this.ignoreScanningKeyList, event?.key),
          );
        }),
        rx.filter((events: KeyboardEvent[]) => {
          return (
            !isEmpty(events) && gte(size(events), DEFAULT_VALID_SCANNING_LENGTH)
          );
        }),
      )
      .subscribe((events: KeyboardEvent[]) => {
        this.scanningResult(events, scanResultOption, true);
      });

    return subs;
  }

  public scrollToItem(
    itemIndex: number,
    position: EnsureIndexVisiblePosition = EnsureIndexVisiblePosition.Middle,
  ): void {
    this.gridApi.ensureIndexVisible(itemIndex, position);
  }

  public disappearSelect() {
    if (judgeScanned(this.keyTimes)) {
      const selectContainerEl: HTMLElement =
        document.querySelector('xux-select-menu');
      if (!isNil(selectContainerEl)) {
        selectContainerEl.click();
      }
    }
  }

  public findItemIndex(
    upcCode: string,
    scanOptions: IScanResultOption,
    showMsg: boolean = true,
  ): number[] {
    this.inventoryItems = getAllRowsAfterFilterAndSort(this.gridApi);
    const indexes: number[] = [];
    this.disappearSelect();
    let itemName: string = '';
    forEach(this.inventoryItems, (inventoryItem: RowNode, index: number) => {
      if (!isEmpty(indexes) || isEmpty(upcCode)) return;
      const item = get(inventoryItem, 'data');
      if (includes(map(get(item, 'upc_codes'), toLower), toLower(upcCode))) {
        indexes.push(index);
        this.scannedItemId = get(
          item,
          get(scanOptions, 'customEntityIdPath'),
          get(item, ENTITY_ID_PATH),
        );
        itemName = get(
          item,
          get(scanOptions, 'customNamePath'),
          get(item, 'name'),
        );
        if (lt(index, MAX_SCREEN_SHOW_ITEMS) && !this.firstScanLessFifty) {
          this.firstScanLessFifty = true;
        }
      }
    });
    if (!showMsg || isEmpty(upcCode)) {
      return indexes;
    }
    if (!isEmpty(indexes)) {
      this.throwMessage(
        NotificationType.COMMON,
        I18n.InfoMessages.ItemIsLocated,
        itemName,
      );
    } else {
      const warningMessage = this.i18nService.instant(
        I18n.ErrorMessages.ScannedCodeCanNotLocated,
        { upcCode },
      );
      this.throwMessage(NotificationType.WARNING, warningMessage);
    }

    return indexes;
  }

  public autoFocusCell(index: number, focusColumnKey: string): void {
    const getAllColumns = this.columnApi.getAllColumns();
    // @ts-ignore
    const column: Column = find(getAllColumns, { colId: focusColumnKey });
    if (!isNil(column)) {
      this.gridApi.setFocusedCell(index, column);
    }
  }

  public throwMessage(
    type: NotificationType,
    scanResultMessage: string,
    itemName: string = '',
    timeout: number = 8000,
  ): void {
    this.uiActions.showToasterMessage({
      type,
      body: this.i18nService.instant(scanResultMessage, {
        itemName,
      }),
      timeout,
    });
  }

  public destroyServiceDefinition(): void {
    this.keyTimes = [];
    this.gridApi = null;
    this.columnApi = null;
    this.inventoryItems = [];
    this.scannedItemId = null;
    this.callBack = null;
    this.firstScanLessFifty = false;
  }

  public getFirstItemIndex(
    events: KeyboardEvent[],
    scanOptions: IScanResultOption,
    showMessage: boolean = true,
  ): number {
    const scannedCode: string = chain(events)
      .map('key')
      .filter((key) => !includes([KEY_CODES.ENTER, KEY_CODES.TAB], key))
      .join('')
      .valueOf();
    const findItemIndexes: number[] = this.findItemIndex(
      scannedCode.replace(/Shift|Clear/gi, ''),
      scanOptions,
      showMessage,
    );

    const firstItemIndex: number = head(findItemIndexes);
    if (isNil(firstItemIndex)) {
      return -1;
    }

    return firstItemIndex;
  }

  public scanningResult(
    events: KeyboardEvent[],
    scanOptions: IScanResultOption,
    needCloseDropDown: boolean = false,
  ): void {
    const firstItemIndex = this.getFirstItemIndex(events, scanOptions);
    this.keyTimes = [];
    if (eq(firstItemIndex, -1)) {
      return;
    }
    if (needCloseDropDown) {
      this.closeOpenedDropdown();
    }
    const autoFocusItemIndex: number = get(scanOptions, 'focusOnNextRow')
      ? firstItemIndex + 1
      : firstItemIndex;
    const {
      isCategoryListView,
      focusKeyForTreeModal,
      focusKeyForListModal,
      isCompleted,
    } = scanOptions;

    if (isCategoryListView) {
      this.gridApi.expandAll();
      this.scrollToItem(firstItemIndex);
      if (!isCompleted) {
        this.autoFocusCell(autoFocusItemIndex, focusKeyForTreeModal);
      }
    } else {
      const reRenderOptions: IScanningRenderOptions = {
        firstItemIndex,
        isCompleted,
        autoFocusItemIndex,
        focusKeyForListModal,
      };
      // for list view, first scan less 50 need refresh list
      if (this.firstScanLessFifty) {
        setTimeout(() => {
          this.gridApi.refreshView();
          this.reRenderRowsAndAutoFocus(reRenderOptions);
        }, 0);
      } else {
        this.reRenderRowsAndAutoFocus(reRenderOptions);
      }
    }
  }

  public reRenderRowsAndAutoFocus(renderOptions: IScanningRenderOptions): void {
    const {
      firstItemIndex,
      isCompleted,
      autoFocusItemIndex,
      focusKeyForListModal,
    } = renderOptions;
    this.scrollToItem(firstItemIndex);
    if (!isCompleted) {
      this.autoFocusCell(autoFocusItemIndex, focusKeyForListModal);
    }
  }

  public closeOpenedDropdown(): void {
    const dropdownEle = document.querySelector('.dropdown-menu-list');

    if (!isEmpty(dropdownEle)) {
      document.body.click();
    }
  }
}
